// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react';
import {
    Button,
    Form,
    SpaceBetween,
    Modal,
    Box,
    Checkbox,
    Alert,
    Link,
} from '@cloudscape-design/components';
import RegionProvider from './RegionProvider';
import Breadcrumbs from '../../../Utils/Breadcrumbs';
import {ACCOUNTS, IMPORT, ONBOARDING_GUIDE} from '../../../routes';
import * as R from 'ramda';
import {useAccounts, useAddAccounts} from '../../Hooks/useAccounts';
import {useHistory} from 'react-router-dom';
import TemplateProvider from './TemplateProvider';

const ImportContent = () => {
    const history = useHistory();
    const {addAsync: addAccounts, isLoading} = useAddAccounts();
    const {data: accounts = []} = useAccounts();
    const [regionsToAdd, setRegionsToAdd] = React.useState([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [showConfirm, setShowConfirm] = React.useState(false);
    const [hasConfirmed, setHasConfirmed] = React.useState({
        global: false,
        regional: false,
    });
    const importedRegions = R.reduce(
        (acc, e) =>
            R.concat(
                acc,
                R.chain(region => {
                    return {
                        accountId: e.accountId,
                        accountName: e.name,
                        region: region.name,
                        isIamRoleDeployed: e.isIamRoleDeployed,
                        isManagementAccount: e.isManagementAccount,
                        lastCrawled: e.lastCrawled,
                        onboardingSource: e.onboardingSource,
                    };
                }, e.regions)
            ),
        [],
        accounts
    );
    const byAccount = R.groupBy(e => e.accountId);
    const importAccounts = async () => {
        // Get the account IDs being re-onboarded traditionally
        const accountsBeingReOnboarded = new Set(
            regionsToAdd.map(r => r.accountId)
        );

        // Get accounts that are currently CLI-onboarded but being re-onboarded via frontend
        const cliAccountsBeingReOnboarded = new Set(
            importedRegions
                .filter(r => 
                    r.onboardingSource === 'CLI' && 
                    accountsBeingReOnboarded.has(r.accountId)
                )
                .map(r => r.accountId)
        );

        // Filter out CLI-onboarded regions ONLY for accounts being re-onboarded
        // traditionally. For other accounts, keep their existing regions.
        const filteredImportedRegions = importedRegions.filter(r => {
            // If this account is being re-onboarded traditionally, exclude CLI regions
            if (accountsBeingReOnboarded.has(r.accountId)) {
                return r.onboardingSource !== 'CLI';
            }
            // Otherwise, keep all regions for this account
            return true;
        });

        const mergedRegions = R.mergeWith(
            R.concat,
            byAccount(regionsToAdd),
            byAccount(filteredImportedRegions)
        );

        // users can update the name of the account when importing new regions
        // so we create a map of the most up-to-date name for each account
        const accountNameMap = regionsToAdd.reduce(
            (acc, {accountId, accountName}) => {
                acc[accountId] = accountName;
                return acc;
            },
            {}
        );

        const accounts = Object.values(mergedRegions).map(accInfo => {
            return accInfo.reduce(
                (acc, item) => {
                    const {
                        accountId,
                        accountName,
                        region,
                        isIamRoleDeployed,
                        isManagementAccount,
                        lastCrawled,
                        resourcesRegionMetadata,
                        onboardingSource
                    } = item;

                    // Check if this CLI account is being re-onboarded traditionally
                    const shouldClearCliMetadata = cliAccountsBeingReOnboarded.has(accountId);

                    let onboardingSourceToWrite = 'DISCOVERY';
                    if (shouldClearCliMetadata) {
                        onboardingSourceToWrite = 'DISCOVERY';
                    } else if (onboardingSource === 'CLI') {
                        onboardingSourceToWrite = 'CLI';
                    } else {
                        onboardingSourceToWrite = 'DISCOVERY';
                    }

                    return {
                        accountId,
                        // we have a fallback here because only a subset of imported accounts
                        // will likely be updated so we won't have names for them all
                        name: accountNameMap[accountId] ?? accountName,
                        // Only preserve these fields if NOT transitioning from CLI
                        isManagementAccount: shouldClearCliMetadata
                            ? undefined
                            : (isManagementAccount ?? acc.isManagementAccount),
                        isIamRoleDeployed: shouldClearCliMetadata
                            ? undefined
                            : (isIamRoleDeployed ?? acc.isIamRoleDeployed),
                        lastCrawled: shouldClearCliMetadata
                            ? undefined
                            : (lastCrawled ?? acc.lastCrawled),
                        regions: [...acc.regions, {name: region}],
                        resourcesRegionMetadata: shouldClearCliMetadata
                            ? undefined
                            : resourcesRegionMetadata,
                        onboardingSource: onboardingSourceToWrite,
                    };
                },
                {regions: []}
            );
        });

        return addAccounts(accounts);
    };

    const handleSubmit = () => {
        setSubmitting(true);
        importAccounts()
            .then(() => history.push(ACCOUNTS))
            .finally(setSubmitting(false));
    };

    return (
        <Form
            actions={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button
                        disabled={isLoading}
                        variant="link"
                        onClick={() => history.push(ACCOUNTS)}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={regionsToAdd.length === 0}
                        loading={isLoading || submitting}
                        onClick={() => setShowConfirm(true)}
                        variant="primary"
                    >
                        Import
                    </Button>
                </SpaceBetween>
            }
        >
            <SpaceBetween size="l">
                <Breadcrumbs
                    items={[
                        {text: 'Accounts', href: ACCOUNTS},
                        {text: 'Import', href: IMPORT},
                    ]}
                />
                <Alert
                    type="info"
                    header="Looking for the easiest way to onboard?"
                >
                    <Box>
                        You can onboard accounts in minutes using the{' '}
                        <strong>ResourceLens CLI</strong> — just 2 commands, no admin access needed.
                        Check out the{' '}
                        <Link
                            href={ONBOARDING_GUIDE}
                            onFollow={e => {
                                e.preventDefault();
                                history.push(ONBOARDING_GUIDE);
                            }}
                        >
                            Onboarding Guide
                        </Link>{' '}
                        to get started.
                    </Box>
                </Alert>
                <Alert
                    type="warning"
                    header="Persistent onboarding: Deploy resources first"
                >
                    <Box>
                        If you are using <strong>CloudFormation templates</strong> or <strong>CDK Constructs</strong> for persistent onboarding,
                        you must <strong>first deploy the IAM roles</strong> to your target accounts and regions before importing them here.
                        <br/><br/>
                        <strong>Proper sequence:</strong> Deploy ResourceLens resources → Then import accounts for discovery
                    </Box>
                </Alert>
                <RegionProvider
                    regions={regionsToAdd}
                    setRegions={setRegionsToAdd}
                />
                <Modal
                    onDismiss={() => setShowConfirm(false)}
                    visible={showConfirm}
                    size={'large'}
                    closeAriaLabel="Close modal"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    onClick={() => setShowConfirm(false)}
                                    variant="link"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    disabled={Object.values(hasConfirmed).some(
                                        i => !i
                                    )}
                                    onClick={handleSubmit}
                                    variant="primary"
                                >
                                    Import
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                    header="Import Accounts and Regions"
                >
                    <SpaceBetween size={'s'}>
                        <TemplateProvider />
                        <Alert
                            header={'Confirm ResourceLens Deployment'}
                            type={'info'}
                        >
                            <SpaceBetween size={'xs'}>
                                <Box>
                                    Please confirm that ResourceLens resources have been deployed 
                                    in the required accounts and regions <strong>before</strong> importing 
                                    them for discovery. You can deploy these resources using either the 
                                    CDK Constructs (Option 1) or CloudFormation templates (Option 2) shown above. 
                                    For deploying to multiple accounts and regions, we recommend using{' '}
                                    <Link
                                        external
                                        href="https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/stacksets-concepts.html"
                                    >
                                        AWS CloudFormation StackSets
                                    </Link>
                                    {' '}or CDK Pipelines.
                                </Box>
                                <Checkbox
                                    onChange={({detail}) =>
                                        setHasConfirmed(e => ({
                                            ...e,
                                            global: detail.checked,
                                        }))
                                    }
                                    checked={hasConfirmed.global}
                                >
                                    <strong>
                                        ResourceLens global resources are deployed 
                                        in each of the accounts being imported
                                    </strong>
                                </Checkbox>
                                <Checkbox
                                    onChange={({detail}) =>
                                        setHasConfirmed(e => ({
                                            ...e,
                                            regional: detail.checked,
                                        }))
                                    }
                                    checked={hasConfirmed.regional}
                                >
                                    <strong>
                                        ResourceLens regional resources are deployed 
                                        in every region being imported for each of the listed accounts
                                    </strong>
                                </Checkbox>
                            </SpaceBetween>
                        </Alert>
                    </SpaceBetween>
                </Modal>
            </SpaceBetween>
        </Form>
    );
};

export default ImportContent;
